import { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams, NavLink } from "react-router-dom";
import { X, SlidersHorizontal, ChevronDown, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { FilterPanel } from "@/components/FilterPanel";
import type { FilterState } from "@/components/FilterPanel";
import { products } from "@/data/products";
import { cn } from "@/lib/utils";

type Gender = "" | "donna" | "uomo";

const MAX_PRICE = Math.max(...products.map((p) => p.price), 0);

const sortOptions = [
  { value: "default", label: "In evidenza" },
  { value: "price-asc", label: "Prezzo crescente" },
  { value: "price-desc", label: "Prezzo decrescente" },
  { value: "rating", label: "Più apprezzati" },
];

const allTabs = [
  { value: "", label: "Tutti" },
  { value: "donna", label: "Donna" },
  { value: "uomo", label: "Uomo" },
  { value: "accessori", label: "Accessori" },
  { value: "scarpe", label: "Scarpe" },
];

const genderTabs: Record<"donna" | "uomo", { value: string; label: string }[]> = {
  donna: [
    { value: "", label: "Tutto Donna" },
    { value: "abbigliamento", label: "Abbigliamento" },
    { value: "scarpe", label: "Scarpe" },
    { value: "borse", label: "Borse" },
    { value: "accessori", label: "Accessori" },
  ],
  uomo: [
    { value: "", label: "Tutto Uomo" },
    { value: "abbigliamento", label: "Abbigliamento" },
    { value: "scarpe", label: "Scarpe" },
    { value: "borse", label: "Borse" },
    { value: "accessori", label: "Accessori" },
  ],
};

function getTipo(category: string): string {
  if (category === "scarpe") return "scarpe";
  if (category === "accessori") return "accessori";
  return "abbigliamento";
}

const tabBase =
  "text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors whitespace-nowrap flex-shrink-0";
const tabActive = "bg-stone-900 text-white border-stone-900";
const tabInactive = "bg-white text-stone-500 border-stone-200 hover:border-stone-500";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get("q") ?? "";
  const genderParam = searchParams.get("gender");
  const gender: Gender = genderParam === "donna" || genderParam === "uomo" ? genderParam : "";
  const tab = searchParams.get("tab") ?? "";
  const sort = searchParams.get("sort") ?? "default";
  const badge = searchParams.get("badge") ?? "";

  const filters: FilterState = useMemo(
    () => ({
      categoria: searchParams.get("cat") ?? "",
      subCategoria: searchParams.get("sub") ?? "",
      taglia: searchParams.get("size") ?? "",
      brand: searchParams.get("brand") ?? "",
      colori: (searchParams.get("colors") ?? "").split(",").filter(Boolean),
      prezzoMin: Math.max(0, parseInt(searchParams.get("priceMin") ?? "0", 10) || 0),
      prezzoMax: parseInt(searchParams.get("priceMax") ?? "", 10) || MAX_PRICE,
      scontoMin: parseInt(searchParams.get("discount") ?? "0", 10) || 0,
    }),
    [searchParams]
  );

  // Document title for SEO + bookmark clarity
  useEffect(() => {
    const t = searchQuery
      ? `"${searchQuery}" — Brupa`
      : badge === "SALE"
      ? "Saldi — Brupa Fashion Mall"
      : gender === "donna"
      ? "Donna — Brupa Fashion Mall"
      : gender === "uomo"
      ? "Uomo — Brupa Fashion Mall"
      : "Collezione — Brupa Fashion Mall";
    document.title = t;
  }, [searchQuery, gender, badge]);

  const [sortOpen, setSortOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sortOpen) return;
    function handleOutside(e: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(e.target as Node))
        setSortOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [sortOpen]);

  function updateParams(updates: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next);
  }

  function switchGender(g: Gender) {
    updateParams({
      gender: gender === g ? null : g,
      tab: null,
      cat: null,
      sub: null,
      size: null,
      brand: null,
      colors: null,
      priceMin: null,
      priceMax: null,
      discount: null,
    });
  }

  function setTab(value: string) {
    updateParams({ tab: value || null });
  }

  function setSort(s: string) {
    updateParams({ sort: s === "default" ? null : s });
  }

  function setFilters(f: FilterState) {
    updateParams({
      cat: f.categoria || null,
      sub: f.subCategoria || null,
      size: f.taglia || null,
      brand: f.brand || null,
      colors: f.colori.length > 0 ? f.colori.join(",") : null,
      priceMin: f.prezzoMin > 0 ? String(f.prezzoMin) : null,
      priceMax: f.prezzoMax < MAX_PRICE ? String(f.prezzoMax) : null,
      discount: f.scontoMin > 0 ? String(f.scontoMin) : null,
    });
  }

  function clearAll() {
    setSearchParams(new URLSearchParams());
  }

  const baseProducts = useMemo(() => {
    if (gender === "donna")
      return products.filter((p) => p.gender === "donna" || p.gender === "unisex");
    if (gender === "uomo")
      return products.filter((p) => p.gender === "uomo" || p.gender === "unisex");
    if (!gender && tab === "donna")
      return products.filter((p) => p.gender === "donna" || p.gender === "unisex");
    if (!gender && tab === "uomo")
      return products.filter((p) => p.gender === "uomo" || p.gender === "unisex");
    if (!gender && tab === "accessori") return products.filter((p) => p.category === "accessori");
    if (!gender && tab === "scarpe") return products.filter((p) => p.category === "scarpe");
    return products;
  }, [gender, tab]);

  const filtered = useMemo(() => {
    let list = [...baseProducts];

    if (gender && tab) {
      if (tab === "borse") {
        list = list.filter((p) => p.subCategory === "borse");
      } else {
        list = list.filter((p) => getTipo(p.category) === tab);
      }
    }

    if (filters.categoria) list = list.filter((p) => getTipo(p.category) === filters.categoria);
    if (filters.subCategoria) list = list.filter((p) => p.subCategory === filters.subCategoria);
    if (filters.taglia) list = list.filter((p) => p.sizes?.includes(filters.taglia));
    if (filters.brand) list = list.filter((p) => p.brand === filters.brand);
    if (filters.colori.length > 0)
      list = list.filter((p) => p.colors?.some((c) => filters.colori.includes(c)));
    list = list.filter((p) => p.price >= filters.prezzoMin && p.price <= filters.prezzoMax);
    if (filters.scontoMin > 0) {
      list = list.filter((p) => {
        if (!p.originalPrice) return false;
        const pct = ((p.originalPrice - p.price) / p.originalPrice) * 100;
        return pct >= filters.scontoMin;
      });
    }
    if (badge) list = list.filter((p) => p.badge === badge);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          (p.brand?.toLowerCase().includes(q) ?? false)
      );
    }

    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);

    return list;
  }, [baseProducts, gender, tab, filters, searchQuery, sort, badge]);

  const activeFilterCount = [
    filters.categoria,
    filters.subCategoria,
    filters.taglia,
    filters.brand,
    ...filters.colori,
    filters.prezzoMin > 0 ? "min" : "",
    filters.prezzoMax < MAX_PRICE ? "max" : "",
    filters.scontoMin > 0 ? "sconto" : "",
    badge ? "badge" : "",
  ].filter(Boolean).length;

  const headingLabel = searchQuery
    ? `"${searchQuery}"`
    : badge === "SALE"
    ? "Saldi"
    : gender === "donna"
    ? "Donna"
    : gender === "uomo"
    ? "Uomo"
    : "Tutta la collezione";

  const currentTabs = gender ? genderTabs[gender] : allTabs;
  const activeTab = tab;

  // Breadcrumb construction
  const crumbs: { label: string; to: string | null }[] = [{ label: "Home", to: "/" }];
  if (gender || badge || searchQuery) {
    crumbs.push({ label: "Shop", to: "/shop" });
    if (gender) crumbs.push({ label: gender.charAt(0).toUpperCase() + gender.slice(1), to: null });
    if (badge === "SALE") crumbs.push({ label: "Saldi", to: null });
    if (searchQuery) crumbs.push({ label: `"${searchQuery}"`, to: null });
  } else {
    crumbs.push({ label: "Shop", to: null });
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Navbar />
      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1.5">
              {crumbs.map((seg, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="size-3 text-stone-300" />}
                  {seg.to ? (
                    <NavLink
                      to={seg.to}
                      className="text-[10px] tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors"
                    >
                      {seg.label}
                    </NavLink>
                  ) : (
                    <span className="text-[10px] tracking-widest uppercase text-stone-800 font-medium">
                      {seg.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          </div>
        </div>

        {/* Page header */}
        <div className="bg-white border-b border-stone-100 py-8 md:py-12 relative">
          <div className="absolute top-3 left-4 sm:left-6 lg:left-8 flex items-center gap-1">
            {(["donna", "uomo"] as const).map((g) => (
              <button
                key={g}
                onClick={() => switchGender(g)}
                className={cn(
                  "text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 border transition-colors",
                  gender === g
                    ? "bg-stone-900 text-white border-stone-900"
                    : "text-stone-400 border-stone-200 hover:border-stone-600 hover:text-stone-700"
                )}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
            {gender && (
              <button
                onClick={() => switchGender("")}
                className="text-stone-300 hover:text-stone-600 transition-colors ml-0.5"
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-2">
              Brupa Fashion Mall
            </p>
            <h1 className="font-semibold text-2xl sm:text-3xl md:text-4xl md:font-bold text-stone-900 tracking-tight">
              {headingLabel}
            </h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
          {/* Toolbar */}
          <div className="mb-8 pb-5 border-b border-stone-200">
            <div className="flex gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden mb-3">
              {currentTabs.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTab(t.value)}
                  className={cn(tabBase, activeTab === t.value ? tabActive : tabInactive)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-stone-400">
                {filtered.length} {filtered.length === 1 ? "articolo" : "articoli"}
              </span>
              <div className="flex items-center gap-2">
                <div className="relative" ref={sortRef}>
                  <button
                    onClick={() => setSortOpen(!sortOpen)}
                    className={cn(
                      "flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-2 border transition-colors",
                      sortOpen
                        ? "bg-stone-900 text-white border-stone-900"
                        : sort !== "default"
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-stone-600 border-stone-200"
                    )}
                  >
                    Ordina
                    <ChevronDown
                      className={cn("size-3 transition-transform", sortOpen && "rotate-180")}
                    />
                  </button>
                  {sortOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 shadow-lg z-20 min-w-48">
                      {sortOptions.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setSort(opt.value);
                            setSortOpen(false);
                          }}
                          className={cn(
                            "w-full text-left text-[10px] tracking-widest uppercase px-4 py-3 border-b border-stone-100 last:border-0 transition-colors",
                            sort === opt.value
                              ? "bg-stone-900 text-white"
                              : "text-stone-600 hover:bg-stone-50"
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className={cn(
                    "flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-3 py-2 border transition-colors",
                    filtersOpen
                      ? "bg-stone-900 text-white border-stone-900"
                      : activeFilterCount > 0
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-stone-600 border-stone-200"
                  )}
                >
                  <SlidersHorizontal className="size-3.5" />
                  Filtri
                  {activeFilterCount > 0 && (
                    <span className="bg-white text-stone-900 rounded-full size-4 text-[9px] font-bold flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Grid */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filtered.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-28">
              <p className="font-medium text-lg sm:text-xl md:text-2xl md:font-semibold text-stone-500 tracking-tight mb-6">
                Nessun risultato trovato
              </p>
              <button
                onClick={clearAll}
                className="text-[10px] tracking-widest uppercase border border-stone-400 text-stone-600 px-8 py-3 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors"
              >
                Vedi tutta la collezione
              </button>
            </div>
          )}
        </div>
      </main>

      {filtersOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm"
            onClick={() => setFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-stone-100 px-5 py-4 flex items-center justify-between z-10">
              <span className="font-semibold text-lg text-stone-900 tracking-tight">Filtri</span>
              <button onClick={() => setFiltersOpen(false)}>
                <X className="size-5 text-stone-500" />
              </button>
            </div>
            <div className="p-5">
              <FilterPanel
                baseProducts={baseProducts}
                filters={filters}
                onChange={setFilters}
              />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-stone-100 px-5 py-4">
              <button
                onClick={() => setFiltersOpen(false)}
                className="w-full bg-stone-900 text-white text-[10px] tracking-widest uppercase py-3.5 hover:bg-stone-700 transition-colors"
              >
                Vedi {filtered.length} risultat{filtered.length === 1 ? "o" : "i"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
